/**
 * <analytics-drill-boundary> — the wall, shown on purpose.
 *
 * The storefront measures its visitors with no consent banner in front of
 * them, and that position is defensible only while this surface stays
 * aggregate. So the network of drills has an edge, and the edge is designed
 * rather than discovered: a figure whose next hop would cross it opens THIS
 * instead of quietly not being pressable, because a number that simply refuses
 * the gesture teaches an operator that the affordance is unreliable, and a
 * number that explains itself teaches them what the system is.
 *
 * TWO WALLS, ONE TREATMENT. The chain stops for a position the product holds
 * (`privacy`) and for a bound the stored row applies so one day's document is
 * the same size at any catalogue size (`bounded`). Each states its own reason
 * in plain words; neither is an error, so neither wears an error tone. A red
 * band would read as a fault the operator might report or retry. This is the
 * system working exactly as designed, and a calm surface is what says so.
 *
 * A DOOR IS A PRESSED FIGURE. It leaves as the same `drill` event a figure's
 * own press does, so the panel around it — the one piece that knows its depth —
 * opens it at the level it was pressed from, and this body needs to know
 * nothing about where in the chain it sits.
 *
 * A WALL WITH A DOOR BESIDE IT. Every refusal ends with what the surface WILL
 * show. A refusal that leaves you at a dead end reads as the product hiding
 * something; a refusal that hands you the nearest true answer reads as the
 * product knowing where its own line is.
 */
import "@mhmo91/schmancy/list";
import "@mhmo91/schmancy/tone-dot";
import { SchmancyElement, schmancyElement } from "@mhmo91/schmancy/mixins";
import { css, html } from "lit";
import { property } from "lit/decorators.js";
import { repeat } from "lit/directives/repeat.js";
import { LocalizeMixin } from "../../i18n/localize.ts";
import type { DrillAnswer, DrillKind } from "./drill.state.ts";
import { phraseText } from "./drill.state.ts";

type BoundaryAnswer = Extract<DrillAnswer, { body: "boundary" }>;

/** Each door wears the glyph of the ANSWER it leads to, never a generic chart
 *  mark — identical icons beside different offers would say the rows are
 *  interchangeable when the whole point is that they are not. */
const DOOR_ICON: Readonly<Record<DrillKind, string>> = {
	parts: "donut_small",
	provenance: "calculate",
	records: "receipt_long",
	boundary: "block",
};

const WALL_ICON: Readonly<Record<BoundaryAnswer["reason"], string>> = {
	privacy: "lock_person",
	bounded: "unfold_less",
};

@schmancyElement("analytics-drill-boundary")
export class AnalyticsDrillBoundary extends LocalizeMixin(SchmancyElement) {
	static styles = [
		css`
			:host {
				display: block;
			}
		`,
	];

	@property({ attribute: false }) answer!: BoundaryAnswer;

	override render() {
		return html`
			<div class="flex flex-col gap-5">
				<div class="flex flex-col gap-3">
					<schmancy-tone-dot
						class="w-11 h-11"
						variant="soft"
						color="neutral"
						icon=${WALL_ICON[this.answer.reason]}
						label=${this.t("admin.analytics.drill.wallDotLabel")}
					></schmancy-tone-dot>
					<schmancy-typography type="title" token="sm">
						${phraseText(this.answer.lead, this)}
					</schmancy-typography>
					<schmancy-typography
						type="body"
						token="md"
						class="text-surface-onVariant"
					>
						${phraseText(this.answer.refusal, this)}
					</schmancy-typography>
				</div>

				<div class="flex flex-col gap-2">
					<schmancy-typography type="label" token="md">
						${this.t("admin.analytics.drill.wallInstead")}
					</schmancy-typography>
					<schmancy-list>
						${repeat(
							this.answer.instead,
							(door) => phraseText(door.label, this),
							(door) => html`
								<schmancy-list-item
									rounded
									.label=${phraseText(door.label, this)}
									@click=${() =>
										this.dispatchEvent(
											new CustomEvent("drill", {
												detail: door.open,
												bubbles: true,
												composed: true,
											}),
										)}
								>
									<schmancy-icon slot="leading">
										${DOOR_ICON[door.open.kind]}
									</schmancy-icon>
									${phraseText(door.label, this)}
								</schmancy-list-item>
							`,
						)}
					</schmancy-list>
				</div>
			</div>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"analytics-drill-boundary": AnalyticsDrillBoundary;
	}
}
